import type { HTMLAttributeAnchorTarget, MouseEvent } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { deferWithLoadingAnimation } from '#/utils/ui/routeLoadingAnimation';
import { getUrlWithoutOrigin } from '#/utils/url/getUrlWithoutOrigin';
import { isUrlWithProtocol } from '#/utils/url/isUrlWithProtocol';
import { useVersionUpdateRequired } from '#/zustand-stores/appVersionStore';

type Args = {
  to: string;
  state: Record<string, unknown> | undefined;
  onClick: ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  target: HTMLAttributeAnchorTarget | undefined;
  reloadDocument?: boolean;
};

export function useVersionedRouterLinkClick({
  to,
  state,
  onClick,
  target,
  reloadDocument,
}: Args) {
  const { versionUpdateRequired } = useVersionUpdateRequired();

  const normalizedTo = useMemo(
    () => (isUrlWithProtocol(to) ? getUrlWithoutOrigin(to) : to),
    [to],
  );

  const navigate = useNavigate();

  const deferredAppLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (target && target !== '_self') {
      onClick?.(event);
      return;
    }

    event.preventDefault();
    void deferWithLoadingAnimation(async () => {
      onClick?.(event);

      if (versionUpdateRequired && !state) {
        trackClicked('Updating App Version', { url: normalizedTo });
        window.location.href = to;
      } else if (reloadDocument) {
        window.location.href = to;
      } else {
        await navigate(normalizedTo, { state });
      }
    });
  };

  return { normalizedTo, deferredAppLinkClick };
}
